/**
 * CollapsedColumn Component
 *
 * Displays a collapsed column with vertical text and card count
 * Expands on click to show full column content
 */

import React from 'react';
import { Icon, IconName } from '@/icons';
import { useThemeColors, withOpacity, opacity } from '@/styles';
import { layout } from '@/styles/design-tokens';

interface CollapsedColumnProps {
  /** Column ID */
  id: string;
  /** Column name to display vertically */
  name: string;
  /** Optional color indicator */
  color?: string;
  /** Number of cards in this column */
  cardCount: number;
  /** Callback when user clicks to expand */
  onClick: () => void;
}

export default function CollapsedColumn({
  name,
  color,
  cardCount,
  onClick,
}: CollapsedColumnProps) {
  const colors = useThemeColors();

  const handleClick = () => {
    onClick();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className="flex-shrink-0 h-full cursor-pointer collapsed-column-hover transition-collapse rounded-xl"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Expand ${name} column with ${cardCount} cards`}
      aria-expanded="false"
      style={{
        width: layout.kanban.columnCollapsed,
        backgroundColor: colors.offWhite,
      }}
    >
      <div className="h-full flex flex-col items-center justify-start py-6 px-2 gap-4">
        {/* Color indicator */}
        {color && (
          <div
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: color }}
            aria-hidden="true"
          />
        )}

        {/* Vertical column name */}
        <div
          className="writing-mode-vertical text-sm font-semibold flex-1 select-none"
          style={{ color: colors.text.primary }}
        >
          {name}
        </div>

        {/* Card count badge */}
        <div
          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium"
          style={{
            backgroundColor: withOpacity(colors.primary, opacity.medium),
            color: colors.primary,
          }}
          aria-label={`${cardCount} cards`}
        >
          {cardCount}
        </div>

        {/* Expand indicator */}
        <Icon
          name={IconName.CHEVRON_RIGHT}
          size="sm"
          color={colors.text.secondary}
          className="flex-shrink-0"
          aria-hidden="true"
        />
      </div>
    </div>
  );
}
